import { CustomizationOptionDto } from '../models/customization/customization-option.dto';
import { String } from '../../_core/utils/utils.string';
import { Evaluate } from "../../_core/utils/utils.evaluate";
import { CustomizationSelectDto } from "../models/customization/customization.dto";
import { MergeTags } from "../../_core/utils/utils.merge-tags";

export class CustomizationOptionUtils {
	public static create(option: Partial<CustomizationOptionDto>): CustomizationOptionDto {
		return {
			id: option.id || new Date().getTime().toString() + Math.random().toString(36).substring(2, 15),
			name: option.name,
			label: option.label || 'Label',
			value: option.value || String.slugify(option.label),
			layer: option.layer,
			layers: option.layers || [],
			styles: option.styles || "",
			metadata: option.metadata || {},
			creativeUnits: option.creativeUnits || [],
			variables: option.variables,
			visibilityConditions: option.visibilityConditions || null,
			resetStylesToDefault: option.resetStylesToDefault,
			transformUnit: option.transformUnit ? { ...option.transformUnit } : undefined
		};
	}

	/**
	 * 1. Reparse mapping and visibility conditions for options
	 * 2. Filter out options that are not supposed to be visible
	 * @param customization
	 * @param options
	 * @param mergeTagState
	 */
	public static processOptions(customization: CustomizationSelectDto, options: CustomizationOptionDto[], mergeTagState: { [key: string]: any }) {
		const reparsedOptions = CustomizationOptionUtils.reparseOptions(customization, options, mergeTagState);
		return CustomizationOptionUtils.getVisibleOptions(reparsedOptions, mergeTagState);
	}

	/**
	 * Evaluate the visibility conditions for each option and filter out the ones that are not supposed to be visible.
	 * @param options
	 * @param mergeState
	 */
	public static getVisibleOptions(options: CustomizationOptionDto[], mergeState: any): CustomizationOptionDto[] {
		return options?.filter(option => {
			if (option.visibilityConditions) {
				// console.log('Evaluating visibility conditions', option.visibilityConditions, mergeState);
				return Evaluate.evaluateMultiple(option.visibilityConditions, {
					...mergeState,
					option
				});
			} else {
				return true;
			}
		})
	}

	/**
	 * Compare two options to see if they should be treated as equal.
	 * @param a
	 * @param b
	 * @param strict If true, compare all properties. If false, compare only value and label.
	 */
	public static areOptionsEqual(a: CustomizationOptionDto, b: CustomizationOptionDto, strict: boolean = true) {
		if (!a && !b) {
			return true;
		}
		if (!a || !b) {
			return false;
		}

		if (a.value !== b.value || a.label !== b.label) {
			return false;
		}

		if (strict) {
			if (a.name !== b.name) {
				return false;
			}

			if (a.layers?.length !== b.layers?.length) {
				return false;
			}

			// remove the ID from the comparison as it differs between the same option saved and newly loaded
			const valueLayers = { ...a.layers, id: undefined };
			const optionLayers = { ...b.layers, id: undefined };

			if (JSON.stringify(valueLayers) !== JSON.stringify(optionLayers)) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Reparse the mapping and visibility conditions for each option using the merge tag state.
	 * @param customization
	 * @param options
	 * @param mergeTagState
	 */
	public static reparseOptions(customization: CustomizationSelectDto, options: CustomizationOptionDto[], mergeTagState: { [key: string]: any }) {
		return options?.map(option => {
			let mapping: any = {};
			try {
				mapping = JSON.parse(
					MergeTags.applyMergeTagsToString(JSON.stringify(customization.dynamicLink.mapping), {...option.metadata, ...mergeTagState})
				);
			} catch (e) {
				console.warn('Unable to parse dynamic link mapping', e);
			}

			return CustomizationOptionUtils.create({
				...option,
				id: option.id,
				label: option.label,
				...mapping,
				visibilityConditions: mapping.visibilityConditions,
				transformUnit: mapping.transformUnit ?? option.transformUnit
			});
		});
	}
}
